import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { formatDateTime } from "../../../globalFunctions/DateTimeDisplayCleaner";
import RepresentativeEditModal from "./RepresentativeModal";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

export default function RepresentativeUserTable() {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const userId = currentUser?.id;

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [selectedUser, setSelectedUser] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadUsersAndRoles();
  }, [userId]);

  const handleEdit = (user, company) => {
    setSelectedUser(user);
    setIsOpenModal(true);
  };

  const handleSave = (updatedUser) => {
    loadUsersAndRoles();
    setIsOpenModal(false);
  };

  const loadUsersAndRoles = async () => {
    setLoading(true);
    try {
      const userResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/Companies`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": userId,
          },
        }
      );

      if (!userResponse.ok) throw new Error("Erreur chargement utilisateurs");
      const usersData = await userResponse.json();
      setUsers(usersData);

      const rolesResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/User/Roles`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": userId,
          },
        }
      );

      if (rolesResponse.ok) {
        const rolesData = await rolesResponse.json();
        setAvailableRoles(rolesData);
      } else {
        console.warn("Impossible de charger les rôles.");
      }
    } catch (err) {
      console.error(err);
      setError("Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setSelectedUser(null);
    setIsOpenModal(true);
  };

  return (
    <div className="p-6 space-y-6 text-primary">
      <button
        onClick={() => navigate(-1)}
        className="btn bg-primary text-white rounded-full flex items-center gap-2"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        {t("global.back")}
      </button>
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">Mes utilisateurs</h1>
        <div className="flex w-full md:w-auto md:ml-auto gap-2">
          <input
            type="text"
            placeholder="Rechercher par nom ou courriel..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input input-bordered w-full md:w-64 "
          />
          <button
            onClick={handleAddUser}
            className="btn bg-primary text-white rounded-full"
          >
            {t("adminUserTable.addButton")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center">Chargement...</div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : users.length === 0 ? (
        <div className="text-center text-gray-500">
          Aucun utilisateur trouvé.
        </div>
      ) : (
        <>
          <h2 className="font-bold text-lg">
            {"Représentant : " + currentUser.email}
          </h2>

          <div className="overflow-x-auto border rounded-lg bg-base-100 shadow-sm">
            <table className="table table-zebra w-full">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Contact</th>
                  <th>Téléphone</th>
                  <th>Statut</th>
                  <th>Créé le</th>
                  <th>Compagnies</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users
                  .filter(
                    (u) =>
                      u.email.toLowerCase().includes(search.toLowerCase()) ||
                      u.fullName?.toLowerCase().includes(search.toLowerCase())
                  )
                  .map((user) => (
                    <tr key={user.id}>
                      <td>{user.fullName || "-"}</td>
                      <td>{user.email || "-"}</td>
                      <td>{user.companies?.[0]?.phoneNumber || "-"}</td>
                      <td>
                        {user.companies?.some((c) => c.isActive) ? (
                          <span className="badge badge-success">Active</span>
                        ) : (
                          <span className="badge badge-error">Inactive</span>
                        )}
                      </td>
                      <td>{formatDateTime(user.createdAt)}</td>
                      <td>
                        {user.companies?.map((c) => c.name).join(", ") || "-"}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleEdit(user)}
                        >
                          Modifier
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {isOpenModal && (
        <RepresentativeEditModal
          key={selectedUser?.id || "new"}
          isOpen={isOpenModal}
          onClose={() => setIsOpenModal(false)}
          onSave={handleSave}
          user={selectedUser}
          availableRoles={availableRoles}
          currentUserId={userId}
        />
      )}
    </div>
  );
}
